import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<
    'success' | 'failed' | 'processing'
  >('processing');

  const transactionId = searchParams.get('transaction_id');
  const paymentIntent = searchParams.get('payment_intent');
  const redirectStatus = searchParams.get('checkout-status');
  const returnUrlFromQuery = searchParams.get('returnUrl');

  // Get returnUrl from location state (passed from payment page) or query params (from Stripe redirect)
  const returnUrl =
    (location.state as { returnUrl?: string })?.returnUrl ||
    returnUrlFromQuery ||
    '/';

  useEffect(() => {
    // Simulate checking payment status
    // In a real app, you'd verify this with your backend
    const timer = setTimeout(() => {
      if (redirectStatus === 'succeeded' || paymentIntent) {
        setPaymentStatus('success');
      } else if (redirectStatus === 'fail') {
        setPaymentStatus('failed');
      } else {
        setPaymentStatus('success'); // Default to success if we have a transaction_id
      }
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [redirectStatus, paymentIntent]);

  const handleContinue = () => {
    // Navigate back to the entity details page without reloading
    navigate(returnUrl, { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-gray-100 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Processing Payment
          </h2>
          <p className="text-gray-600">
            Please wait while we confirm your payment...
          </p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 dark:from-light-active contrast:from-light-active to-emerald-100 dark:to-light-active contrast:to-light-active flex items-center justify-center px-4">
        <div className="bg-gray-100 rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Payment Successful!
            </h2>
            <p className="text-gray-600 mb-6">
              Your subscription has been activated successfully.
            </p>

            {transactionId && (
              <div className="bg-gray-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-500 mb-1">Transaction ID</p>
                <p className="text-sm font-mono text-gray-900 break-all">
                  {transactionId}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={handleContinue}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
              >
                Continue
                <ArrowRight className="w-5 h-5" />
              </button>

              <p className="text-xs text-gray-500">
                A confirmation email has been sent to your registered email
                address.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 dark:from-light-active to-rose-100 dark:to-light-active flex items-center justify-center px-4">
      <div className="bg-gray-100 rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <XCircle className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Payment Failed
          </h2>
          <p className="text-gray-600 mb-6">
            We couldn't process your payment. Please try again or contact
            support.
          </p>

          <div className="space-y-3">
            <button
              onClick={() => navigate(-1)}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200"
            >
              Try Again
            </button>

            <button
              onClick={handleContinue}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-all duration-200"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
